import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Timer, ListChecks, Play, Lock, Trophy } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SectionTitle } from "@/components/Section";
import AuthGateModal from "@/components/AuthGateModal";

const TABS = [
  { key: "all", label: "All" },
  { key: "prelims", label: "Prelims" },
  { key: "current-affairs", label: "Current Affairs" },
];

export default function MockTests() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [tests, setTests] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [gateOpen, setGateOpen] = useState(false);
  const cat = params.get("category") || "all";

  useEffect(() => {
    api.get("/tests").then(({ data }) => setTests(data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (user && typeof user === "object") api.get("/me/attempts").then(({ data }) => setAttempts(data)).catch(() => {});
  }, [user]);

  const filtered = useMemo(
    () => (cat === "all" ? tests : tests.filter((t) => (t.category || "prelims") === cat)),
    [tests, cat]
  );

  const setCat = (k) => { if (k === "all") setParams({}); else setParams({ category: k }); };

  const start = (t) => {
    if (!(user && typeof user === "object")) { setGateOpen(true); return; }
    navigate(`/mock-tests/${t.id}`);
  };
  const bestScore = (testId) => {
    const a = attempts.filter((x) => x.test_id === testId);
    return a.length ? Math.max(...a.map((x) => x.score)) : null;
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <SectionTitle sub="UPSC-style mocks · +2 for correct, −0.66 for wrong · timed & login-gated" testId="mock-tests-title">
        Mock Tests
      </SectionTitle>

      <div className="mb-6 flex flex-wrap gap-2" data-testid="mock-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setCat(t.key)}
            className={`rounded-sm border px-4 py-1.5 text-sm font-semibold transition-colors ${cat === t.key ? "border-[#1F3A68] bg-[#1F3A68] text-white" : "border-[#D1D5DB] bg-white text-[#1F3A68] hover:bg-gray-50"}`}
            data-testid={`mock-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.length === 0 && <div className="gov-card p-8 text-center text-sm text-gray-500" data-testid="tests-empty">No tests in this category yet. Check back soon!</div>}
        {filtered.map((t) => {
          const best = bestScore(t.id);
          return (
            <div key={t.id} className="gov-card flex flex-col" data-testid={`test-card-${t.id}`}>
              <div className="gov-card-header flex items-center justify-between rounded-t-sm px-4 py-2.5 font-serif text-base font-bold">
                <span className="truncate">{t.title}</span>
              </div>
              <div className="flex flex-1 flex-col p-4">
                <span className={`w-fit rounded-sm px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${t.category === "current-affairs" ? "bg-[#D97706]/10 text-[#D97706]" : "bg-[#1F3A68]/10 text-[#1F3A68]"}`}>
                  {t.category === "current-affairs" ? "Current Affairs" : "Prelims"}
                </span>
                <p className="mt-2 text-sm text-gray-600">{t.description}</p>
                <div className="mt-3 flex flex-wrap gap-4 text-xs text-gray-500">
                  <span className="flex items-center gap-1"><ListChecks size={14} /> {t.question_count} questions</span>
                  <span className="flex items-center gap-1"><Timer size={14} /> {t.duration_minutes} min</span>
                </div>
                {best !== null && (
                  <div className="mt-3 flex w-fit items-center gap-1.5 rounded-sm bg-[#16A34A]/10 px-2.5 py-1 text-xs font-semibold text-[#16A34A]">
                    <Trophy size={13} /> Best: {best} / {(t.question_count * 2).toFixed(0)}
                  </div>
                )}
                <button onClick={() => start(t)} className="mt-4 flex items-center justify-center gap-2 rounded-sm bg-[#1F3A68] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#0B2545]" data-testid={`start-test-${t.id}`}>
                  {user && typeof user === "object" ? <Play size={15} /> : <Lock size={14} />}
                  {best !== null ? "Re-attempt" : "Start Test"}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <AuthGateModal open={gateOpen} onOpenChange={setGateOpen} nextPath="/mock-tests" />
    </div>
  );
}
